// Productions, venues and events — the read-only slate catalogue.
//
// All are revision-controlled by the catalog domain. They remain coherent in
// memory until the manifest reports a new catalog revision.

import { api } from '@/api/client';
import { buildQs } from '@/api/query-string';
import { defineQuery } from '@/api/resource';
import type {
  EventsListResponse,
  ProductionResponse,
  ProductionsListResponse,
  RunningProductionsSummaryResponse,
  VenueResponse,
  VenuesListResponse,
} from '@/api/types';

export interface ProductionsQuery {
  tag?: string;
  venue_id?: string;
  q?: string;
}

export interface VenuesQuery {
  q?: string;
}

export interface EventsQuery {
  // Param names must match the API's `/events` ListQuery (date_from/date_to,
  // not from/to) or the server silently ignores them.
  date_from?: string;
  date_to?: string;
  production_id?: string;
  venue_id?: string;
  status?: string;
  q?: string;
  limit?: number;
}

export const productionsList = defineQuery({
  namespace: 'authoritative',
  name: 'productions.list',
  domains: ['catalog'],
  request: (q: ProductionsQuery, signal) =>
    api.get<ProductionsListResponse>(`/productions${buildQs(q)}`, { signal }),
  unwrap: (r) => r.productions,
});

export const productionById = defineQuery({
  namespace: 'authoritative',
  name: 'productions.byId',
  domains: ['catalog'],
  keyOf: (id: string) => [id],
  request: (id: string, signal) =>
    api.get<ProductionResponse>(`/productions/${encodeURIComponent(id)}`, { signal }),
  unwrap: (r) => r.production,
});

export const productionsRunningSummary = defineQuery({
  namespace: 'derived',
  name: 'productions.runningSummary',
  // `running` changes with the ET calendar even when no production row changes.
  volatile: true,
  keyOf: () => [],
  request: (_: void, signal) =>
    api.get<RunningProductionsSummaryResponse>('/productions/running-summary', { signal }),
  unwrap: (r) => r.summary,
});

export const venuesList = defineQuery({
  namespace: 'authoritative',
  name: 'venues.list',
  domains: ['catalog'],
  request: (q: VenuesQuery, signal) =>
    api.get<VenuesListResponse>(`/venues${buildQs(q)}`, { signal }),
  unwrap: (r) => r.venues,
});

export const venueById = defineQuery({
  namespace: 'authoritative',
  name: 'venues.byId',
  domains: ['catalog'],
  keyOf: (id: string) => [id],
  request: (id: string, signal) =>
    api.get<VenueResponse>(`/venues/${encodeURIComponent(id)}`, { signal }),
  unwrap: (r) => r.venue,
});

export const eventsList = defineQuery({
  namespace: 'authoritative',
  name: 'events.list',
  domains: ['catalog'],
  request: (q: EventsQuery, signal) =>
    api.get<EventsListResponse>(`/events${buildQs(q)}`, { signal }),
  unwrap: (r) => r.events,
});
